<script setup lang="ts">
import { ref, watch } from 'vue';
import { GuestService, type GuestBackupMetadata } from '@/api/guest/guestService';
import { useToast } from 'primevue/usetoast';

const props = defineProps<{
  isVisible: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'restored'): void;
}>();

const toast = useToast();
const backups = ref<GuestBackupMetadata[]>([]);
const loading = ref(false);
const restoringId = ref<string | null>(null);

const formatBackupDate = (createdAt: string): string => {
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) {
    return createdAt;
  }

  return parsed.toLocaleString('pl-PL');
};

const loadBackups = async (): Promise<void> => {
  try {
    loading.value = true;
    backups.value = await GuestService.getGuestBackups();
  } catch (error) {
    console.error('Failed to load guest backups:', error);
    toast.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się pobrać kopii zapasowych.',
      life: 4000,
    });
  } finally {
    loading.value = false;
  }
};

const onRestore = async (backup: GuestBackupMetadata): Promise<void> => {
  const confirmed = confirm(
    `Przywrócić kopię z ${formatBackupDate(backup.createdAt)} (${backup.guestCount} gości)? Bieżąca lista zostanie zastąpiona.`,
  );

  if (!confirmed) {
    return;
  }

  try {
    restoringId.value = backup.id;
    await GuestService.restoreGuestBackup(backup.id);
    toast.add({
      severity: 'success',
      summary: 'Przywrócono',
      detail: `Przywrócono ${backup.guestCount} gości z kopii zapasowej.`,
      life: 4000,
    });
    emit('restored');
    emit('close');
  } catch (error) {
    console.error('Failed to restore guest backup:', error);
    toast.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się przywrócić kopii zapasowej.',
      life: 4000,
    });
  } finally {
    restoringId.value = null;
  }
};

const onClose = (): void => {
  emit('close');
};

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      void loadBackups();
    }
  },
);
</script>

<template>
  <div
    v-if="isVisible"
    class="guest-backup-dialog"
  >
    <div class="guest-backup-dialog__content">
      <div class="guest-backup-dialog__header">
        <h2>Kopie zapasowe listy gości</h2>
        <button
          class="guest-backup-dialog__close-button"
          @click="onClose"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>

      <p class="guest-backup-dialog__description">
        Przed każdym zapisem tworzona jest automatyczna kopia. Możesz przywrócić wcześniejszą wersję listy gości.
      </p>

      <div
        v-if="loading"
        class="guest-backup-dialog__loading"
      >
        Ładowanie kopii...
      </div>

      <div
        v-else-if="backups.length === 0"
        class="guest-backup-dialog__empty"
      >
        Brak dostępnych kopii zapasowych.
      </div>

      <ul
        v-else
        class="guest-backup-dialog__list"
      >
        <li
          v-for="backup in backups"
          :key="backup.id"
          class="guest-backup-dialog__item"
        >
          <div class="guest-backup-dialog__item-info">
            <span class="guest-backup-dialog__item-date">{{ formatBackupDate(backup.createdAt) }}</span>
            <span class="guest-backup-dialog__item-count">{{ backup.guestCount }} gości</span>
          </div>
          <button
            class="guest-backup-dialog__restore-button"
            :disabled="restoringId === backup.id"
            @click="onRestore(backup)"
          >
            {{ restoringId === backup.id ? 'Przywracanie...' : 'Przywróć' }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.guest-backup-dialog {
  position: fixed;
  inset: 0;
  background-color: rgba($black, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  &__content {
    background-color: $white;
    padding: 2rem;
    border-radius: 8px;
    width: 100%;
    max-width: 640px;
    max-height: 80vh;
    overflow-y: auto;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__close-button {
    background: none;
    border: none;
    cursor: pointer;
    color: $brown-300;
    font-size: 1.5rem;
  }

  &__description {
    color: $brown-300;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  &__loading,
  &__empty {
    padding: 1rem 0;
    color: $brown-300;
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    background-color: $white-200;
    border-radius: 4px;
  }

  &__item-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__item-date {
    font-weight: 600;
  }

  &__item-count {
    color: $brown-300;
    font-size: 0.95rem;
  }

  &__restore-button {
    padding: 0.75rem 1.25rem;
    border: none;
    border-radius: 4px;
    background-color: $brown-200;
    color: $white;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}
</style>
